import { SelfContainedCommand, CommandExecutionContext } from "../../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../../types/standardToolResults";
import { createFileLogger } from "../../../utils/logger";
import { getApplicationForEntity } from "../../../services/applicationRegistry";

const log = createFileLogger('APP_BUILDER_PUBLISH');

export const appBuilderPublishCommand: SelfContainedCommand = {
  id: 'app_builder_publish',
  description: `Publish an app_project as a runnable River app.

Compiles the project and registers it so it can be opened and run anywhere in River.

**WORKFLOW:**
1. Gets all files from app_project
2. Compiles the code
3. Creates a published App entity
4. Registers in app registry
5. App is now available to open and use

**EXAMPLES:**

{ "entityId": "app_proj_xyz123", "appName": "My Calculator", "appId": "my_calculator" }

**RETURNS:** Published app details
`,
  autonomyLevel: 'ask',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'app_project entity ID to publish',
        required: true
      },
      {
        name: 'appName',
        type: 'string',
        description: 'Name for the published app',
        required: true
      },
      {
        name: 'appId',
        type: 'string',
        description: 'Unique app ID (lowercase, no spaces)',
        required: true
      },
      {
        name: 'description',
        type: 'string',
        description: 'App description',
        required: false
      }
    ]
  },
  
  examples: {
    good: [],
    bad: []
  },
  
  performance: {
    expectedDuration: 3000,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { entityId, appName, appId, description = '' } = params;
    
    log.info('[app_builder_publish] Publishing app:', { entityId, appName, appId });
    
    try {
      // Step 1: Get AppBuilderApplication instance
      const app = await getApplicationForEntity(entityId);
      if (!app) {
        return createErrorResult('Project not found', 'Could not load project', 'execution');
      }
      
      // Step 2: Get all files
      const files = (app as any).getFilesForCompilation();
      
      log.info('[app_builder_publish] Got files:', Object.keys(files));
      
      // Step 3: Check for existing app in registered_apps and FORCE clear cached bundle
      log.info('[app_builder_publish] Checking for existing app...');
      
      let existingAppDbId: string | null = null;
      try {
        const { API_BASE_URL } = await import('../../../api/config');
        const { default: fetchService } = await import('../../../services/fetchService');
        
        const existingApps = await fetchService(
          `${API_BASE_URL}/apps/available?appId=${appId}`
        );
        
        const existingApp = Array.isArray(existingApps)
          ? existingApps.find((a: any) => a.appId === appId && !a.isSystem)
          : null;

        if (existingApp?._id) {
          existingAppDbId = existingApp._id;
          log.info('[app_builder_publish] Found existing app, FORCE clearing cached bundle:', existingAppDbId);
          
          try {
            await fetchService(`${API_BASE_URL}/apps/${existingAppDbId}`, {
              method: 'PUT',
              body: JSON.stringify({ 'skeleton.compiled': null }),
            });
            log.info('[app_builder_publish] ✅ Cached bundle cleared, will recompile');
          } catch (clearError) {
            log.error('[app_builder_publish] Failed to clear cache:', clearError);
          }
        }
      } catch (queryError) {
        log.warn('[app_builder_publish] Could not check for existing app:', queryError);
      }
      
      // Step 4: Compile the app
      log.info('[app_builder_publish] Compiling...');
      
      const compileResult = await (app as any).compile();
      
      if (!compileResult.success) {
        return createErrorResult(
          'Compilation failed',
          compileResult.error || 'Failed to compile app',
          'execution'
        );
      }

      // Website and Next.js projects cannot be published as standalone apps —
      // their compile result is a service-worker VFS marker, not a JS bundle.
      if (
        compileResult.compiledCode === '@@SERVICE_WORKER_MODE@@' ||
        compileResult.mode === 'service-worker'
      ) {
        return createErrorResult(
          'Cannot publish website project',
          'Website and Next.js projects cannot be published as standalone apps. Use app_builder_publish only for React app projects.',
          'execution'
        );
      }
      
      log.info('[app_builder_publish] Compilation successful', {
        hasCompiledCode: !!compileResult.compiledCode,
        hasComponent: !!compileResult.Component
      });
      
      // Step 5: Create or update published App entity
      log.info('[app_builder_publish] Creating/updating App entity...');
      
      // Use simple phosphor icon as default (guaranteed to render)
      const defaultIcon = {
        type: 'phosphor',
        phosphorIcon: 'AppWindow',
        phosphorWeight: 'duotone',
        color: '#3b82f6'
      };
      
      const appSkeletonData = {
        '@type': 'App',
        appId: appId,
        name: appName,
        description: description,
        icon: defaultIcon,
        category: 'user-created',
        version: '1.0.0',
        isCore: false,
        source: {
          files: files,
          entryPoint: 'src/App.tsx'
        },
        compiled: {
          // Store the compiled bundle for faster loading
          bundle: compileResult.compiledCode || null,
          bundledAt: new Date().toISOString()
        },
        publishing: {
          isPublished: true,
          publishedAt: new Date().toISOString(),
          isDraft: false,
          visibility: 'private'
        },
        sourceProjectId: entityId,
        entityTypes: [],
        component: 'DynamicAppWrapper',
        requiresProps: false,
        requiresEntity: false
      };
      
      const { API_BASE_URL } = await import('../../../api/config');
      const { default: fetchService } = await import('../../../services/fetchService');
      let appEntity: any;
      
      if (existingAppDbId) {
        // Update existing app in registered_apps
        log.info('[app_builder_publish] Updating existing app in registered_apps:', existingAppDbId);
        
        await fetchService(`${API_BASE_URL}/apps/${existingAppDbId}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: appName,
            icon: defaultIcon,
            skeleton: appSkeletonData,
          })
        });
        
        appEntity = { _id: existingAppDbId, skeleton: appSkeletonData };
        log.info('[app_builder_publish] ✅ App updated in registered_apps');
      } else {
        // Register new app in registered_apps
        log.info('[app_builder_publish] Registering new app in registered_apps');
        appEntity = await fetchService(`${API_BASE_URL}/apps`, {
          method: 'POST',
          body: JSON.stringify({ skeleton: appSkeletonData }),
        });
        log.info('[app_builder_publish] ✅ App registered in registered_apps');
      }
      
      log.info('[app_builder_publish] App entity created/updated:', appEntity._id);
      
      // ✅ AUTO-INSTALL: Install app for the publishing user
      // This makes the app appear in Dock, Quick Launcher, etc.
      try {
        await fetchService(`${API_BASE_URL}/apps/install`, {
          method: 'POST',
          body: JSON.stringify({ appId: appId })
        });
        
        log.info('[app_builder_publish] ✅ App auto-installed for user');
        
        // Invalidate app cache so it appears immediately
        const { requestCacheService } = await import('../../../services/requestCacheService');
        requestCacheService.invalidate('installed-apps');
        
      } catch (installError) {
        log.warn('[app_builder_publish] Failed to auto-install app:', installError);
        // Don't fail publish if install fails - user can manually install
      }
      
      // Note: Don't register component in app registry
      // Apps will be loaded dynamically via DynamicAppWrapper when opened
      log.info('[app_builder_publish] App will load dynamically when opened');
      
      const duration = Date.now() - startTime;
      
      return createSuccessResult(
        `Published "${appName}" - App installed and ready to use!`,
        {
          appId: appId,
          appEntityId: appEntity._id,
          projectEntityId: entityId,
          name: appName,
          description: description,
          fileCount: Object.keys(files).length,
          installed: true
        },
        {
          metadata: {
            executionTime: duration
          }
        }
      );
    } catch (error: any) {
      log.error('[app_builder_publish] Error:', error);
      return createErrorResult(
        'Failed to publish app',
        error.message || 'Unknown error',
        'execution'
      );
    }
  }
};
